import { useState } from 'react';
import { Button, FormField, Select } from '@/shared/components';
import type { ObjectApartmentSummary } from '@/shared/api';
import { roomOptions, wholeFlatOptions } from './flatFamilyModel';
import { useWholeFlatLink } from './useWholeFlatLink';
import { WholeFlatPreviewResult } from './WholeFlatPreviewResult';

interface Props {
  /** `join`: dieses Apartment wird Zimmer; `addRoom`: es bekommt ein Zimmer dazu. */
  mode: 'join' | 'addRoom';
  propertyId: string;
  propertyName: string;
  apartments: ObjectApartmentSummary[];
  onDone?: () => void;
  onCancel?: () => void;
}

const nameOf = (apartment: ObjectApartmentSummary) => apartment.adminDisplayName ?? apartment.name;

/** Auswahl im selben Objekt, Prüfung durch den Server, dann Verknüpfen — für beide Richtungen. */
export function WholeFlatLinkFlow({
  mode,
  propertyId,
  propertyName,
  apartments,
  onDone,
  onCancel,
}: Props) {
  const [selectedId, setSelectedId] = useState('');
  const options =
    mode === 'join'
      ? wholeFlatOptions(apartments, propertyId)
      : roomOptions(apartments, propertyId);
  const selected = options.find((option) => option.id === selectedId) ?? null;
  const roomId = mode === 'join' ? propertyId : (selected?.id ?? null);
  const wholeFlatId = mode === 'join' ? (selected?.id ?? null) : propertyId;
  const { preview, link, failure, clearFailure } = useWholeFlatLink(roomId, wholeFlatId, () => {
    setSelectedId('');
    onDone?.();
  });
  const label = mode === 'join' ? 'Gehört zu Gesamtwohnung' : 'Zimmer hinzufügen';

  if (options.length === 0) {
    return (
      <div className="mt-4">
        <p className="text-ink-soft text-sm">
          {mode === 'join'
            ? 'Im Objekt gibt es kein weiteres Apartment, das Gesamtwohnung sein kann.'
            : 'Im Objekt gibt es kein freies Apartment, das als Zimmer dazukommen kann.'}
        </p>
        {onCancel && (
          <Button type="button" variant="ghost" className="mt-2 min-h-11" onClick={onCancel}>
            Schließen
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <FormField label={label} className="max-w-md">
        <Select
          value={selectedId}
          onChange={(event) => {
            clearFailure();
            setSelectedId(event.target.value);
          }}
        >
          <option value="">— Apartment wählen —</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {nameOf(option)}
            </option>
          ))}
        </Select>
      </FormField>
      {selected && (
        <WholeFlatPreviewResult
          preview={preview}
          roomName={mode === 'join' ? propertyName : nameOf(selected)}
          wholeFlatName={mode === 'join' ? nameOf(selected) : propertyName}
          saving={link.isPending}
          failure={failure}
          onLink={() => link.mutate()}
        />
      )}
      {onCancel && (
        <Button type="button" variant="ghost" className="mt-2 min-h-11" onClick={onCancel}>
          Abbrechen
        </Button>
      )}
    </div>
  );
}
